
import Button from 'primevue/button';
import MultiSelect from 'primevue/multiselect';
import {
  defineComponent, onMounted, Ref, ref, SetupContext, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
// CampaignsCategoryEnum no longer needed — replaced by tags system
import EditorModal from '@/components/modals/EditorModal.vue';
import Loader from '@/components/layout/Loader.vue';
import { asInt, showToastError, showToastSuccess } from '@/helpers';
import { setRefreshScenariosList } from '@/composables/AutomatedScenarios';
import { getNowFormattedByTimezone } from '@/helpers/Date';
import { openAutomationCampaignEditor } from '@/views/workflows/AutomationCampaignsActions/useAutomationCampaignsActions';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';
import { store } from '@/store';
import { getCampaignsAutomationBaseList } from '@/composables/workflows/Workflow';
import { UserState } from '@/composables/User';
import { nestCall, NestApiType } from '@/composables/nestApi';
import RequireIntegrationDialog from '@/components/integrations/RequireIntegrationDialog.vue';
import type { IntegrationOwnedItem } from '@/types/integrations';
import { useIntegrationGating } from '@/composables/integrations/useIntegrationGating';
import { useIntegrationCollections } from '@/composables/integrations/useIntegrationCollections';
import DrilldownBanner from '@/components/integrations/DrilldownBanner.vue';
import { pickLocalizedText } from '@/utils/localized-text';
import IntegrationCollectionCard from '@/components/integrations/IntegrationCollectionCard.vue';

interface CampaignsAutomationBase extends IntegrationOwnedItem {
  id: number;
  name: Record<string, string>;
  legend: Record<string, string>;
  tags: string[];
  categories?: string; // legacy compat
  path: string;
  images_templates: string[] | null;
}

type TagCategory = 'channel' | 'feature' | 'type' | 'objective';

const TAG_CATEGORY_MAP: Record<string, TagCategory> = {
  // Channels
  Email: 'channel',
  SMS: 'channel',
  Push: 'channel',
  WhatsApp: 'channel',
  Popup: 'channel',
  // Features
  Gamification: 'feature',
  'Bon de reduction': 'feature',
  'A/B Test': 'feature',
  Segmente: 'feature',
  'Cross-canal': 'feature',
  // Scenario types
  'Panier abandonne': 'type',
  'Produit consulte': 'type',
  Anniversaire: 'type',
  Bienvenue: 'type',
  Reactivation: 'type',
  Fidelite: 'type',
  'Incitation newsletter': 'type',
  // Objectives
  Conversion: 'objective',
  Fidelisation: 'objective',
  Retention: 'objective',
  Acquisition: 'objective',
};

export default defineComponent({
  name: 'CampaignAutomationSelectorV2',

  components: {
    TemplateHtmlPreviewSlider,
    Loader,
    EditorModal,
    Button,
    MultiSelect,
    RequireIntegrationDialog,
    IntegrationCollectionCard,
    DrilldownBanner,
  },

  emits: {
    'on-close-dialog': null,
  },

  setup(props: { }, context: SetupContext) {
    const { t, locale } = useI18n();

    const currentLocale = computed(() => locale.value || 'fr');

    const getLocalizedText = (textObj: Record<string, string> | undefined, fallback = ''): string => {
      if (!textObj || typeof textObj !== 'object') return fallback;
      return pickLocalizedText(textObj, currentLocale.value) || fallback;
    };

    const isListLoading = ref(false);
    const initialList: Ref<CampaignsAutomationBase[]> = ref([]);
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);
    const searchQuery = ref('');

    // Integration gating (modal state, pre-flight check, backend safety
    // net, dynamic author options) — shared with the bulk and template
    // pickers via `useIntegrationGating`.
    const selectedAuthors = ref<string[]>([]);
    const {
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
      gatePreflight,
      gateBackendResponse,
      authorFilterOptionsFor,
    } = useIntegrationGating();

    const authorFilterOptions = authorFilterOptionsFor(() => initialList.value);

    // ----- Split + search for the visual author filter -----

    /** Live-search over the unified author list (ShopiMind + integrations). */
    const authorSearchQuery = ref('');

    /** All author options (ShopiMind + integrations at the same level),
     *  filtered by the search input. Integration options carry a `count`. */
    const filteredAuthorOptions = computed(() => {
      const q = authorSearchQuery.value.trim().toLowerCase();
      if (!q) return authorFilterOptions.value;
      return authorFilterOptions.value.filter(
        (o) => o.label.toLowerCase().includes(q)
          || o.value.toLowerCase().includes(q),
      );
    });

    /**
     * Toggle behaviour for the visual author cards (replaces the multi-
     * select). Clicking a card adds/removes its slug from `selectedAuthors`.
     */
    const toggleAuthor = (value: string) => {
      const idx = selectedAuthors.value.indexOf(value);
      if (idx >= 0) {
        selectedAuthors.value.splice(idx, 1);
      } else {
        selectedAuthors.value.push(value);
      }
    };

    /**
     * Resolve the icon URL for an author option. For ShopiMind we use a
     * font-awesome icon (rendered in the template). For integrations we
     * dig back into `initialList.value` and grab the first item whose
     * `integration_slug` matches — that's where the icon lives.
     */
    const getAuthorIcon = (slug: string): string | undefined => {
      if (slug === 'shopimind') return undefined;
      const match = initialList.value.find(
        (item) => item.integration_slug === slug,
      );
      return match?.integration_icon_url ?? undefined;
    };

    // Tags filter
    const selectedTags = ref<string[]>([]);

    const tagGroups = computed(() => {
      const selected = selectedTags.value;

      // Count tags only on items that already match all selected tags
      const tagCounts = new Map<string, number>();
      for (const item of initialList.value) {
        const itemTags = item.tags || [];
        // Item must have all currently selected tags
        if (!selected.every((t) => itemTags.includes(t))) continue;
        for (const tag of itemTags) {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        }
      }

      const groups: { key: TagCategory; label: string; tags: { value: string; count: number }[] }[] = [
        { key: 'type', label: t('automatedScenarios.workflowSelector.filters.tagGroups.type'), tags: [] },
        { key: 'channel', label: t('automatedScenarios.workflowSelector.filters.tagGroups.channel'), tags: [] },
        { key: 'feature', label: t('automatedScenarios.workflowSelector.filters.tagGroups.feature'), tags: [] },
        { key: 'objective', label: t('automatedScenarios.workflowSelector.filters.tagGroups.objective'), tags: [] },
      ];

      for (const [value, count] of tagCounts) {
        const cat = TAG_CATEGORY_MAP[value] || 'feature';
        const group = groups.find((g) => g.key === cat);
        if (group) group.tags.push({ value, count });
      }

      for (const group of groups) {
        group.tags.sort((a, b) => a.value.localeCompare(b.value));
      }

      return groups.filter((g) => g.tags.length > 0);
    });

    const toggleTag = (tag: string) => {
      const idx = selectedTags.value.indexOf(tag);
      if (idx >= 0) {
        selectedTags.value.splice(idx, 1);
      } else {
        selectedTags.value.push(tag);
      }
    };

    const getTagCategory = (tag: string): string => TAG_CATEGORY_MAP[tag] || 'feature';

    const closeModal = () => {
      context.emit('on-close-dialog');
    };

    const handleStartFromScratch = async () => {
      store.commit('general/showTheSpinner');

      try {
        const idShop = UserState.activeShop?.id ?? 0;

        if (!idShop) {
          showToastError(t('automatedScenarios.create_action.error'));
          store.commit('general/hideTheSpinner');
          return;
        }

        // Generate scenario name with current date
        const name = `${t('automationPrefix')} - ${getNowFormattedByTimezone()}`;

        // Create empty automation campaign via API
        const result = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/campaigns/automations',
          {},
          '',
          { name },
        );

        if (result && result.data && result.data.id) {
          // Use shared function to open the editor with all checks
          await openAutomationCampaignEditor(
            result.data.id,
            result.data.name,
            () => context.emit('on-close-dialog'),
          );
        } else {
          showToastError(result?.message || t('automatedScenarios.create_action.error'));
          store.commit('general/hideTheSpinner');
        }
      } catch (error) {
        console.error('Campaign creation error:', error);
        showToastError(t('automatedScenarios.create_action.error'));
        store.commit('general/hideTheSpinner');
      }
    };

    // Computed filtered list
    const filteredList = computed(() => {
      if (!initialList.value.length) return [];

      return initialList.value.filter((baseWorkflow) => {
        const tags = baseWorkflow.tags || [];

        // Filter by selected tags (AND: all selected tags must be present)
        const matchedTags = selectedTags.value.length === 0
          || selectedTags.value.every((tag) => tags.includes(tag));

        // Filter by search
        const matchedSearch = searchQuery.value.trim() === ''
          || getLocalizedText(baseWorkflow.name).toLowerCase().includes(searchQuery.value.toLowerCase())
          || getLocalizedText(baseWorkflow.legend).toLowerCase().includes(searchQuery.value.toLowerCase());

        // Filter by author — "shopimind" matches native items (no
        // integration_slug); an integration slug matches its items.
        const authorKey = baseWorkflow.author === 'integration'
          ? baseWorkflow.integration_slug ?? ''
          : 'shopimind';
        const matchedAuthor = selectedAuthors.value.length === 0
          || selectedAuthors.value.includes(authorKey);

        return matchedTags && matchedSearch && matchedAuthor;
      });
    });

    // Collapse an integration's many predefined items into one collection card
    // (anti-flood). Clicking a collection drills into that integration.
    const { displayItems, drilledInIntegration } = useIntegrationCollections(
      () => filteredList.value,
      selectedAuthors,
    );
    const drillIntoIntegration = (slug: string) => {
      selectedAuthors.value = [slug];
    };
    const clearAuthorFilter = () => {
      selectedAuthors.value = [];
    };

    const handleChoose = async (record: CampaignsAutomationBase) => {
      // Pre-flight: bail out if the integration isn't active on this shop.
      if (gatePreflight(record)) return;

      store.commit('general/showTheSpinner');

      try {
        const idShop = UserState.activeShop?.id ?? 0;

        if (!idShop) {
          showToastError(t('automatedScenarios.create_action.error'));
          store.commit('general/hideTheSpinner');
          return;
        }

        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/create-from-base',
          {},
          '',
          {
            idShop,
            type: 'campaign_automation',
            baseId: record.path,
          },
        );

        // Backend safety net (shop may have deactivated the integration
        // between page load and click).
        if (gateBackendResponse(result, record)) {
          store.commit('general/hideTheSpinner');
          return;
        }

        if (result && result.success && result.id) {
          showToastSuccess(t('automatedScenarios.create_action.success'));
          setRefreshScenariosList(true);
          await openAutomationCampaignEditor(
            asInt(result.id),
            result.name || '',
            () => context.emit('on-close-dialog'),
          );
        } else {
          showToastError(result?.message || t('automatedScenarios.create_action.error'));
          store.commit('general/hideTheSpinner');
        }
      } catch (error) {
        console.error('Workflow creation error:', error);
        showToastError(t('automatedScenarios.create_action.error'));
        store.commit('general/hideTheSpinner');
      }
    };

    const openGalleryHtmlPreview = async (record: CampaignsAutomationBase) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/gallery-html-preview',
          {},
          '',
          { path: record.path },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(t('automatedScenarios.workflowSelector.preview.error'));
        }
      } catch {
        showToastError(t('automatedScenarios.workflowSelector.preview.error'));
      }
      store.commit('general/hideTheSpinner');
    };

    const closeHtmlPreview = () => {
      htmlPreviewData.value = null;
    };

    onMounted(async () => {
      isListLoading.value = true;

      try {
        const idShop = UserState.activeShop?.id ?? undefined;
        const result: CampaignsAutomationBase[] = await getCampaignsAutomationBaseList(idShop);

        if (result) {
          initialList.value = result;
        }
      } catch (error) {
        showToastError(t('automatedScenarios.list.error'));
      } finally {
        isListLoading.value = false;
      }
    });

    return {
      t,
      filteredList,
      displayItems,
      drilledInIntegration,
      drillIntoIntegration,
      clearAuthorFilter,
      htmlPreviewData,
      isListLoading,
      searchQuery,
      closeModal,
      handleChoose,
      openGalleryHtmlPreview,
      closeHtmlPreview,
      handleStartFromScratch,
      selectedAuthors,
      authorFilterOptions,
      filteredAuthorOptions,
      authorSearchQuery,
      toggleAuthor,
      getAuthorIcon,
      selectedTags,
      tagGroups,
      toggleTag,
      getTagCategory,
      getLocalizedText,
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
    };
  },
});
